import type { Notification } from "@circle-react/types/Notification";
import { IconButton } from "@circleco/compass/components/IconButton";
import { Menu } from "@circleco/compass/components/Menu";

import { t } from "@/i18n-js/instance";

import { useNotificationsActions } from "../../../useHeaderNotificationsApi";
import {
  FOLLOWABLE_NOTIFICATION_TYPES,
  NOTIFICATION_ACTION_MAPPER,
  NOTIFICATION_ARCHIVED_STATUS,
} from "../constants";

export interface ActionsComponentProps {
  notification: Notification;
}

export const ActionsComponent = ({ notification }: ActionsComponentProps) => {
  const canUnfollowPost = notification.post_follower_id;
  const canFollowPost =
    FOLLOWABLE_NOTIFICATION_TYPES.includes(notification.notifiable_type) &&
    !notification.post_follower_id;
  const canArchiveNotification =
    notification.notification_group !== NOTIFICATION_ARCHIVED_STATUS;
  const hasNotificationBeenRead = notification.read_at;

  const {
    mutate: performNotificationAction,
    isLoading: isNotificationActionBeingPerformed,
  } = useNotificationsActions();

  const isLiveAction = notification.action === "live_stream_went_live";
  const shouldSkipNotificationDropdown =
    !canUnfollowPost &&
    !canArchiveNotification &&
    hasNotificationBeenRead !== null &&
    isLiveAction;

  if (shouldSkipNotificationDropdown) {
    return null;
  }

  const options = [
    {
      hidden: Boolean(hasNotificationBeenRead),
      label: t("notifications.actions.mark_as_read"),
      disabled: isNotificationActionBeingPerformed,
      onClick: () =>
        performNotificationAction({
          id: notification.id,
          action: NOTIFICATION_ACTION_MAPPER.markAsRead,
        }),
    },
    {
      hidden: !canUnfollowPost,
      label: t("notifications.actions.unfollow_post"),
      disabled: isNotificationActionBeingPerformed,
      onClick: () =>
        performNotificationAction({
          postId: notification.post_id,
          followerId: notification.post_follower_id,
          action: NOTIFICATION_ACTION_MAPPER.unfollow,
        }),
    },
    {
      hidden: !canFollowPost,
      label: t("notifications.actions.follow_post"),
      disabled: isNotificationActionBeingPerformed,
      onClick: () =>
        performNotificationAction({
          postId: notification.post_id,
          action: NOTIFICATION_ACTION_MAPPER.follow,
        }),
    },
    {
      hidden: !canArchiveNotification,
      label: t("notifications.actions.archive"),
      disabled: isNotificationActionBeingPerformed,
      onClick: () =>
        performNotificationAction({
          id: notification.id,
          action: NOTIFICATION_ACTION_MAPPER.archive,
        }),
    },
  ];

  const areAllOptionsHidden = options.every(option => option.hidden);

  if (areAllOptionsHidden) {
    return <div className="hidden size-8 group-hover:block" />;
  }

  return (
    <div
      className="hidden size-8 group-hover:block has-[[data-popup-open]]:block"
      onClick={event => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onKeyDown={event => event.stopPropagation()}
      aria-hidden="true"
    >
      <Menu
        align="end"
        side="bottom"
        options={options}
        trigger={
          <IconButton
            icon="dot-menu"
            variant="ghost"
            size="sm"
            aria-label={t("actions")}
          />
        }
      />
    </div>
  );
};
